import React, { memo, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import {
  formatNip,
  GrossChoice,
  IApplicationTermsDraft,
  RATE_UNITS,
  useTeacherBillingEntities,
} from 'core/hooks';
import { InputLabel, ISelectOption, Select, Text } from 'atoms';

interface IApplicationTermsFieldsProps {
  /** Чьи реквизиты предлагать; нет — выбор контрагента пуст. */
  teacherId?: string | null;
  value: IApplicationTermsDraft;
  onChange: (next: IApplicationTermsDraft) => void;
  disabled?: boolean;
  /**
   * Новая заявка: контрагент по умолчанию — основной у преподавателя
   * (как делает сервер), пока человек не выбрал сам.
   */
  preselectDefault?: boolean;
}

/**
 * ТЗ 0114 (#158) — финансовые условия заявки в окнах «Добавить / Править
 * заявку»: единица ставки (занятие / час / курс), net / gross и контрагент
 * из реквизитов преподавателя («кто выставит нам фактуру»). VAT — у
 * контрагента, в заявке не дублируется. Сумма и валюта — прежние поля окна.
 */
export const ApplicationTermsFields = memo(
  ({
    teacherId,
    value,
    onChange,
    disabled = false,
    preselectDefault = false,
  }: IApplicationTermsFieldsProps) => {
    const { t } = useTranslation();
    const { entities, loading } = useTeacherBillingEntities(teacherId);

    const unitOptions: ISelectOption<string>[] = useMemo(
      () =>
        RATE_UNITS.map((unit) => ({
          value: unit,
          label: t(`teacherBilling.unit.${unit}`),
        })),
      [t],
    );
    const grossOptions: ISelectOption<string>[] = useMemo(
      () => [
        { value: '', label: t('teacherBilling.gross.unknown') },
        { value: 'net', label: t('teacherBilling.gross.net') },
        { value: 'gross', label: t('teacherBilling.gross.gross') },
      ],
      [t],
    );
    const entityOptions: ISelectOption<string>[] = useMemo(
      () => [
        { value: '', label: t('teacherBilling.terms.noEntity') },
        ...entities.map((entity) => ({
          value: entity.id,
          label: [
            entity.legalName,
            entity.nip ? `NIP ${formatNip(entity.nip)}` : '',
            entity.isDefault ? t('teacherBilling.defaultBadge') : '',
          ]
            .filter(Boolean)
            .join(' · '),
        })),
      ],
      [entities, t],
    );

    // Основной контрагент — сразу в новой заявке (один раз).
    const preselected = useRef(false);
    useEffect(() => {
      if (!preselectDefault || preselected.current || value.billingEntityId) {
        return;
      }
      const main = entities.find((entity) => entity.isDefault);
      if (!main) return;
      preselected.current = true;
      onChange({ ...value, billingEntityId: main.id });
    }, [entities, onChange, preselectDefault, value]);

    const pick = (options: ISelectOption<string>[], key: string) =>
      options.find((option) => option.value === key) ?? null;

    return (
      <Wrap data-testid="application-terms">
        <Title size="sm">{t('teacherBilling.terms.title')}</Title>
        <Grid>
          <div>
            <InputLabel>{t('teacherBilling.terms.rateUnit')}</InputLabel>
            <Select
              value={pick(unitOptions, value.rateUnit || 'lesson')}
              options={unitOptions}
              type="default"
              name="rateUnit"
              onChange={(option) =>
                onChange({
                  ...value,
                  rateUnit:
                    (option as ISelectOption<string> | null)?.value || 'lesson',
                })
              }
              isDisabled={disabled}
            />
          </div>
          <div>
            <InputLabel>{t('teacherBilling.terms.netGross')}</InputLabel>
            <Select
              value={pick(grossOptions, value.rateIsGross)}
              options={grossOptions}
              type="default"
              name="rateIsGross"
              onChange={(option) =>
                onChange({
                  ...value,
                  rateIsGross: ((option as ISelectOption<string> | null)
                    ?.value || '') as GrossChoice,
                })
              }
              isDisabled={disabled}
            />
          </div>
          <Wide>
            <InputLabel isOptional>
              {t('teacherBilling.terms.billingEntity')}
            </InputLabel>
            <Select
              value={pick(entityOptions, value.billingEntityId)}
              options={entityOptions}
              type="default"
              name="billingEntityId"
              onChange={(option) =>
                onChange({
                  ...value,
                  billingEntityId:
                    (option as ISelectOption<string> | null)?.value || '',
                })
              }
              isDisabled={disabled || loading}
            />
            {!loading && entities.length === 0 ? (
              <Hint size="sm">{t('teacherBilling.terms.noEntitiesHint')}</Hint>
            ) : null}
          </Wide>
        </Grid>
      </Wrap>
    );
  },
);

ApplicationTermsFields.displayName = 'ApplicationTermsFields';

const Wrap = styled.div`
  margin-top: 24px;
  width: 100%;
`;

const Title = styled(Text)`
  display: block;
  margin-bottom: 12px;
  font-weight: 600;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  column-gap: 32px;
  row-gap: 24px;
  width: 100%;
`;

const Wide = styled.div`
  grid-column: 1 / 3;
`;

const Hint = styled(Text)`
  display: block;
  margin-top: 6px;
  color: var(--k-fg-616e78);
`;
